/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { useTranslation } from 'react-i18next';
import { usePageDesignerMode } from '@salesforce/storefront-next-runtime/design/react/core';
import { Component } from '@/lib/decorators/component';
import { AttributeDefinition } from '@/lib/decorators/attribute-definition';
import { RegionDefinition } from '@/lib/decorators';
import { Link } from '@/components/link';
import { cn } from '@/lib/utils';

@Component('announcementMessage', {
    name: 'Announcement Message',
    group: 'priceshoes_content',
    description: 'A single message, with an optional inline link, shown inside the Announcement Carousel (pre-header)',
})
@RegionDefinition([])
// oxlint-disable-next-line react/only-export-components -- oxlint flags the co-exported Page Designer metadata class; eslint-plugin-react-refresh does not
export class AnnouncementMessageMetadata {
    @AttributeDefinition({ name: 'Message', type: 'string' })
    message!: string;

    @AttributeDefinition({ name: 'Link URL', type: 'url', required: false })
    linkUrl?: string;

    @AttributeDefinition({ name: 'Link Text', type: 'string', required: false })
    linkText?: string;
}

interface AnnouncementMessageProps {
    message?: string;
    linkUrl?: string;
    linkText?: string;
    className?: string;
}

/**
 * Message slide for the Announcement Carousel: text plus an optional inline link (same pattern as
 * the Announcement Banner CTA). Colors are inherited from the carousel container (configured once
 * for all messages), so this component carries no styling attributes.
 */
export default function AnnouncementMessage({ message, linkUrl, linkText, className }: AnnouncementMessageProps) {
    const { t } = useTranslation('common');
    const { isDesignMode } = usePageDesignerMode();
    const text = message?.trim();

    if (!text) {
        // Authoring affordance only: on the live storefront an empty message renders nothing.
        if (!isDesignMode) return null;
        return (
            <p data-slot="announcement-message" className={cn('text-center', className)}>
                {t('announcementMessage.emptyTitle')}
            </p>
        );
    }

    return (
        <p data-slot="announcement-message" className={cn('text-center', className)}>
            {text}
            {linkUrl && linkText && (
                <>
                    {' '}
                    <Link to={linkUrl} className="underline font-medium whitespace-nowrap">
                        {linkText}
                    </Link>
                </>
            )}
        </p>
    );
}
